import React, { useState, useEffect } from 'react';

const OurJourney = () => {
  // Timeline data with all 9 years (2016-2024)
  const timelineItems = [
    {
      year: "2016",
      subtitle: "Foundation",
      details: ["Company incorporated", "First robo advisory platform for RIAs"]
    },
    {
      year: "2017",
      subtitle: "Growth",
      details: ["Mobile App launched", "Milestone of 25,000 users"]
    },
    {
      year: "2018",
      subtitle: "Expansion",
      details: ["Launched Equity Research", "'JEWEL' focused on top 350 companies"]
    },
    {
      year: "2019",
      subtitle: "Advisory",
      details: ["Equity advisory portfolio launched", "First funding round"]
    },
    {
      year: "2020",
      subtitle: "Regulation",
      details: ["Corporate RIA registration", "ISO 9001, ISO27001 certification"]
    },
    {
      year: "2021",
      subtitle: "Innovation",
      details: ["Launched 'Spark' small cap portfolio", "Tech Platform for BFSI sector"]
    },
    {
      year: "2022",
      subtitle: "Enterprise",
      details: ["Enterprise SaaS pilot", "B2B2C partnerships initiated"]
    },
    {
      year: "2023",
      subtitle: "PMS",
      details: ["PMS subsidiary incorporated", "SEBI PMS Registration obtained"]
    },
    {
      year: "2024",
      subtitle: "Diversification",
      details: ["MFD vertical launched", "GEM Strategy implementation"]
    }
  ];

  // State to track current active item
  const [activeIndex, setActiveIndex] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 640);
      setIsTablet(window.innerWidth >= 640 && window.innerWidth < 1024);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // Split into two rows for desktop
  const firstRowItems = timelineItems.slice(0, 5); // 2016-2020
  const secondRowItems = timelineItems.slice(5);   // 2021-2024

  // Automatic rotation of highlighted items - only on larger screens
  useEffect(() => {
    if (isMobile || isTablet) return;
    
    const totalItems = timelineItems.length;
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex === null || prevIndex >= totalItems - 1) ? 0 : prevIndex + 1);
    }, 3000); // Change active item every 3 seconds

    return () => clearInterval(interval);
  }, [isMobile, isTablet]);

  // Function to determine if an item is active
  const isActive = (rowIndex, itemIndex) => {
    if (isMobile || isTablet) {
      return activeIndex === itemIndex; // For mobile, just use the direct index
    } else {
      const actualIndex = rowIndex === 0 ? itemIndex : itemIndex + firstRowItems.length;
      return activeIndex === actualIndex;
    }
  };

  // Mobile/Tablet Timeline View
  if (isMobile || isTablet) {
    return (
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 py-8 bg-white overflow-hidden">
        <h2 className="text-2xl sm:text-3xl font-bold text-[#113262] mb-4 text-center">Our Journey</h2>
        <p className="text-center mt-2 text-gray-600 max-w-lg mx-auto pb-6">Building trust through transparency since 2016.</p>
        
        {/* Vertical Timeline */}
        <div className="relative pt-4 pb-8 max-w-md mx-auto">
          {/* Vertical Timeline Line */}
          <div className="absolute left-6 top-0 bottom-0 w-1 bg-[#1C52A0]"></div>
          
          {timelineItems.map((item, index) => (
            <div 
              key={index} 
              className={`relative mb-6 sm:mb-8 pl-16 transition-all duration-300 cursor-pointer ${
                activeIndex === index ? 'z-10' : ''
              }`}
              onClick={() => setActiveIndex(activeIndex === index ? null : index)}
            >
              {/* Year Circle */}
              <div 
                className={`absolute left-0 w-12 h-12 rounded-full bg-[#1C52A0] text-white flex items-center justify-center z-10 font-bold text-sm border-4 border-white shadow-md transition-all duration-300 ${
                  activeIndex === index ? 'ring-2 ring-[#F49611]' : ''
                }`}
              >
                {item.year}
              </div>
              
              {/* Card Content */}
              <div 
                className={`bg-blue-100 border rounded-lg ${
                  activeIndex === index ? 'border-[#F49611] shadow-lg' : 'border-blue-200'
                } p-4 transition-all duration-300`}
              >
                <h3 className="text-lg font-medium text-[#1C52A0] mb-2">{item.subtitle}</h3>
                
                {/* Always show details on mobile, or show when active on tablet */}
                {(isMobile || activeIndex === index || isTablet) && (
                  <ul className="text-sm text-gray-700 space-y-2">
                    {item.details.map((detail, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="inline-block h-2 w-2 rounded-full bg-[#F49611] mt-1.5 mr-2 flex-shrink-0"></span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Original desktop view remains unchanged
  return (
    <div className="max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-6 lg:py-8 bg-white overflow-hidden py-12">
      <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#113262] mb-4 text-center mb-10">Our Journey</h2>
      <p className="text-center mt-6 text-gray-600 max-w-2xl mx-auto pb-12">Building trust through transparency since 2016.</p>
      
      {/* First row timeline */}
      <div className="mb-12">
        <div className="flex relative">
          {/* Timeline line starts after first year circle and extends past right edge */}
          <div className="absolute h-1 bg-[#1C52A0] top-4" style={{left: "calc(10% + 10px)", right: "-5%"}}></div>
          
          {firstRowItems.map((item, index) => {
            const active = isActive(0, index);
            
            return (
              <div 
                key={index} 
                className="w-1/5 px-2"
                onMouseEnter={() => setActiveIndex(index)}
              >
                <div className={`relative group ${active ? 'z-10' : ''}`}>
                  {/* Year circle with active/hover effect */}
                  <div className="relative flex justify-center">
                    <div 
                      className={`h-10 w-10 rounded-full bg-[#1C52A0] text-white flex items-center justify-center z-10 font-bold text-sm transition-transform duration-300 ${
                        active ? 'transform scale-110 ring-2 ring-[#F49611]' : 'group-hover:transform group-hover:scale-110'
                      }`}
                    >
                      {item.year}
                    </div>
                  </div>
                  
                  {/* Card with pointy top and active/hover effect */}
                  <div 
                    className={`mt-4 relative transition-transform duration-300 ${
                      active ? 'transform scale-105 shadow-lg' : 'group-hover:transform group-hover:scale-105'
                    }`}
                  >
                    {/* Triangle pointer */}
                    <div className="absolute w-4 h-4 bg-blue-100 rotate-45 left-0 right-0 mx-auto -top-2 border-t border-l border-blue-200"></div>
                    
                    {/* Card content */}
                    <div className={`bg-blue-100 border ${active ? 'border-[#F49611]' : 'border-blue-200'} rounded-lg shadow-md p-4 h-48 flex flex-col pt-4`}>
                      <h3 className="text-lg font-medium text-[#1C52A0] mb-2">{item.subtitle}</h3>
                      <ul className="text-sm text-gray-700 space-y-2 flex-grow">
                        {item.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="inline-block h-2 w-2 rounded-full bg-[#F49611] mt-1.5 mr-2"></span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Second row timeline */}
      <div>
        <div className="flex justify-center relative">
          {/* Custom timeline line for second row (extends past left edge, ends before last year) */}
          <div className="absolute h-1 bg-[#1C52A0] top-4" style={{left: "-5%", right: "calc(12.5% + 5px)"}}></div>
          
          {secondRowItems.map((item, index) => {
            const active = isActive(1, index);
            
            return (
              <div 
                key={index} 
                className="w-1/4 px-2"
                onMouseEnter={() => setActiveIndex(index + firstRowItems.length)}
              >
                <div className={`relative group ${active ? 'z-10' : ''}`}>
                  {/* Year circle with active/hover effect */}
                  <div className="relative flex justify-center">
                    <div 
                      className={`h-10 w-10 rounded-full bg-[#1C52A0] text-white flex items-center justify-center z-10 font-bold text-sm transition-transform duration-300 ${
                        active ? 'transform scale-110 ring-2 ring-[#F49611]' : 'group-hover:transform group-hover:scale-110'
                      }`}
                    >
                      {item.year}
                    </div>
                  </div>
                  
                  {/* Card with pointy top and active/hover effect */}
                  <div 
                    className={`mt-4 relative transition-transform duration-300 ${
                      active ? 'transform scale-105 shadow-lg' : 'group-hover:transform group-hover:scale-105'
                    }`}
                  >
                    {/* Triangle pointer */}
                    <div className="absolute w-4 h-4 bg-blue-100 rotate-45 left-0 right-0 mx-auto -top-2 border-t border-l border-blue-200"></div>
                    
                    {/* Card content */}
                    <div className={`bg-blue-100 border ${active ? 'border-[#F49611]' : 'border-blue-200'} rounded-lg shadow-md p-4 h-48 flex flex-col pt-4`}>
                      <h3 className="text-lg font-medium text-[#1C52A0] mb-2">{item.subtitle}</h3>
                      <ul className="text-sm text-gray-700 space-y-2 flex-grow">
                        {item.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="inline-block h-2 w-2 rounded-full bg-[#F49611] mt-1.5 mr-2"></span>
                            <span>{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default OurJourney;